import type { RunSubmissionWithDaily } from '@cradle7/shared';

const KEY = 'cradle7.submitQueue';
const MAX_QUEUE = 50;

function read(): RunSubmissionWithDaily[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(items: RunSubmissionWithDaily[]): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* quota */ }
}

export function enqueueSubmission(sub: RunSubmissionWithDaily): void {
  const current = read();
  current.push(sub);
  const trimmed = current.length > MAX_QUEUE ? current.slice(current.length - MAX_QUEUE) : current;
  write(trimmed);
}

export function peekQueue(): RunSubmissionWithDaily[] {
  return read();
}

export function queueLength(): number {
  return read().length;
}

export function clearQueue(): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(KEY); } catch {}
}

/**
 * Feed each queued submission to `consume` in insertion order. If `consume`
 * returns true, the submission is removed from the queue. The first time it
 * returns false, draining STOPS and the remainder is preserved for the next
 * attempt — this preserves order, which matters for streak and aggregate
 * correctness on the server.
 */
export async function drainQueue(
  consume: (sub: RunSubmissionWithDaily) => Promise<boolean>
): Promise<void> {
  let items = read();
  while (items.length > 0) {
    const head = items[0]!;
    let ok: boolean;
    try {
      ok = await consume(head);
    } catch {
      ok = false;
    }
    if (!ok) break;
    items = items.slice(1);
    write(items);
  }
}
